import type { ApplicationCourse, ApplicationIntake } from '../api/types';

const humanizeCode = (code: string) =>
  code
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const resolveProfileEnumLabel = (
  code: string | null | undefined,
  other: string | null | undefined,
  labels: Record<string, string>,
): string | null => {
  if (!code) return null;
  if (code === 'other') {
    const custom = other?.trim();
    return custom || labels.other || 'Other';
  }
  return labels[code] ?? humanizeCode(code);
};

export const AWARD_LABELS: Record<string, string> = {
  bachelor: "Bachelor's Degree",
  master: "Master's Degree",
  phd: 'PhD',
  diploma: 'Diploma',
  certificate: 'Certificate',
  other: 'Other',
};

export const SPECIALISATION_LABELS: Record<string, string> = {
  business: 'Business Administration',
  'computer-science': 'Computer Science',
  engineering: 'Engineering',
  medicine: 'Medicine',
  law: 'Law',
  psychology: 'Psychology',
  education: 'Education',
  arts: 'Arts',
  science: 'Science',
  other: 'Other',
};

export const SALUTATION_LABELS: Record<string, string> = {
  mr: 'Mr',
  mrs: 'Mrs',
  ms: 'Ms',
  miss: 'Miss',
  mx: 'Mx',
  dr: 'Dr',
  other: 'Other',
};

export const GENDER_LABELS: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  'non-binary': 'Non-binary',
  'prefer-not-to-say': 'Prefer not to say',
  other: 'Other',
};

export const EMERGENCY_RELATIONSHIP_LABELS: Record<string, string> = {
  father: 'Father',
  mother: 'Mother',
  parent: 'Parent',
  guardian: 'Guardian',
  spouse: 'Spouse',
  partner: 'Partner',
  sibling: 'Sibling',
  friend: 'Friend',
  other: 'Other',
};

export const HOW_DID_YOU_HEAR_LABELS: Record<string, string> = {
  website: 'College Website',
  'social-media': 'Social Media',
  friend: 'Friend/Family',
  teacher: 'Teacher/Professor',
  counselor: 'Career Counselor',
  advertisement: 'Advertisement',
  event: 'College Fair/Event',
  'search-engine': 'Search Engine',
  other: 'Other',
};

export const HOW_DID_YOU_HEAR_OPTIONS = Object.entries(HOW_DID_YOU_HEAR_LABELS).map(
  ([key, label]) => ({ key, label }),
);

export const ENGLISH_LEVEL_LABELS: Record<string, string> = {
  'no-test': 'No test',
  ielts: 'IELTS',
  toefl: 'TOEFL',
  cambridge: 'Cambridge',
  other: 'Other',
};

export const STUDY_FORMAT_LABELS: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  blended: 'Blended',
};

export const STUDY_MODE_LABELS: Record<string, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
};

export const resolveStudyFormatLabel = (
  studyFormat: ApplicationIntake['studyFormat'] | string | null | undefined,
): string | null => {
  if (!studyFormat) return null;
  return STUDY_FORMAT_LABELS[studyFormat] ?? humanizeCode(studyFormat);
};

export const resolveStudyModeLabel = (
  studyMode: ApplicationIntake['studyMode'] | string | null | undefined,
): string | null => {
  if (!studyMode) return null;
  return STUDY_MODE_LABELS[studyMode] ?? humanizeCode(studyMode);
};

export const resolveDurationOfStudy = (
  course: ApplicationCourse | null | undefined,
  intake: ApplicationIntake | null | undefined,
): string | null => {
  if (!course || !intake) return null;

  const months =
    intake.studyMode === 'part-time' ? course.durationPartTime : course.durationFullTime;

  if (months == null) return null;

  const modeLabel = resolveStudyModeLabel(intake.studyMode);
  return modeLabel ? `${months} months (${modeLabel})` : `${months} months`;
};
